import React, { useState } from 'react';
import { api } from '../../lib/api';
import { notify } from '../../lib/notify';

function timeAgo(ts: number) {
  const d = Date.now() / 1000 - ts;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

type Item = {
  id: string;
  displayTitle: string;
  verse?: string | null;
  createdAt: number;
  status: string;
};

export function KanbanCard({
  item,
  selectable,
  selected,
  onSelectChange,
  onRenamed,
}: {
  item: Item;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (id: string, v: boolean) => void;
  onRenamed?: (id: string, title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.displayTitle);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      await api(`/api/tales/${item.id}/rename`, { method: 'PATCH', json: { title } });
      onRenamed?.(item.id, title);
      setEditing(false);
      notify.success('Renamed', 'Tale title updated');
    } catch (e: any) {
      notify.error('Rename failed', e?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 shadow-sm cursor-grab active:cursor-grabbing hover:border-blue-500/40">
      <div className="flex items-start gap-2">
        {selectable && (
          <input
            type="checkbox"
            className="mt-1 accent-blue-500"
            checked={!!selected}
            onChange={(e) => onSelectChange?.(item.id, e.target.checked)}
            aria-label={`Select ${item.displayTitle}`}
            title={`Select ${item.displayTitle}`}
          />
        )}

        <div className="flex-1 min-w-0">
          {!editing ? (
            <>
              <div className="text-sm font-medium line-clamp-2">{item.displayTitle}</div>
              {item.verse && <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{item.verse}</div>}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-md bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-2 py-1 text-sm outline-none focus:border-blue-500"
                aria-label="Edit tale title"
                title="Edit tale title"
                placeholder="Enter tale title"
              />
              <button
                className="p-1 rounded-md bg-blue-600/80 hover:bg-blue-600 text-white"
                disabled={saving}
                onClick={save}
                title="Save"
              >
                ✓
              </button>
              <button
                className="p-1 rounded-md bg-slate-700 hover:bg-slate-600 text-white"
                disabled={saving}
                onClick={() => { setEditing(false); setTitle(item.displayTitle); }}
                title="Cancel"
              >
                ✕
              </button>
            </div>
          )}

          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
            <span>{timeAgo(item.createdAt)}</span>
            <div className="flex items-center gap-3">
              <a href={`/tales/${item.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">Open</a>
              {!editing && (
                <button
                  className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  onClick={() => setEditing(true)}
                >
                  ✏️ Rename
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
